import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  EditButton,
  DeleteButton,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  required,
  BooleanInput,
  ReferenceField,
  BooleanField,
  NumberField,
  useRecordContext,
  TopToolbar,
  CreateButton,
  SimpleList,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  Grid,
  useMediaQuery,
  Theme,
} from '@mui/material';

// 카테고리 레벨 필드 컴포넌트
const CategoryLevelField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const level = record.level || 0;
  const levelText = level === 0 ? '대분류' : level === 1 ? '중분류' : `${level}단계`;
  const levelColor = level === 0 ? 'primary' : level === 1 ? 'default' : 'secondary';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Chip label={levelText} color={levelColor} size="small" />
    </Box>
  );
};

// 카테고리 이름 필드 컴포넌트
const CategoryNameField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const isRootCategory = !record.parentId;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography
        component="span"
        sx={{
          fontWeight: isRootCategory ? 'bold' : 'normal',
          lineHeight: 1.4,
        }}
      >
        {record.name}
      </Typography>
    </Box>
  );
};

// 상품 수 필드 컴포넌트
const ProductCountField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Typography component="span">
        {record.productCount || 0}
      </Typography>
    </Box>
  );
};

// CategoryTree component removed


// 카테고리 목록 액션 버튼
const CategoryListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

// 카테고리 목록
export const CategoryList = () => {
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E' }}>
          카테고리
        </Typography>
      </Box>
      <List title="카테고리" actions={<CategoryListActions />} sort={{ field: 'code', order: 'ASC' }} perPage={11}>
        {isSmall ? (
          <SimpleList
            primaryText={(record) => (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                {record.level > 0 && (
                  <Box sx={{
                    width: 16,
                    borderLeft: '2px solid #e0e0e0',
                    borderBottom: '2px solid #e0e0e0',
                    height: 12,
                    ml: 1,
                  }} />
                )}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: record.level === 0 ? 700 : 400,
                    fontSize: record.level === 0 ? '1rem' : '0.9rem',
                    color: record.level === 0 ? '#1C1C1E' : '#666',
                  }}
                >
                  {record.name}
                </Typography>
                <Chip
                  label={record.level === 0 ? '대분류' : '중분류'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    bgcolor: record.level === 0 ? '#03C75A' : '#f5f5f5',
                    color: record.level === 0 ? '#fff' : '#666',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Box>
            )}
            secondaryText={(record) => (
              <Box sx={{
                ml: record.level > 0 ? 4 : 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Typography component="span" variant="body2" color="textSecondary">
                  상품 {record.productCount || 0}개
                </Typography>
              </Box>
            )}
            tertiaryText={(record) => (
              <Chip
                label={record.isActive ? '활성' : '비활성'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: record.isActive ? '#E8F5E9' : '#f5f5f5',
                  color: record.isActive ? '#4CAF50' : '#9e9e9e',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            rowSx={(record) => ({
              bgcolor: record.level === 0 ? '#fafafa' : '#fff',
              borderLeft: record.level === 0 ? '4px solid #03C75A' : '4px solid transparent',
              '&:hover': {
                bgcolor: record.level === 0 ? '#f0f0f0' : '#fafafa',
              },
            })}
            linkType="edit"
          />
        ) : (
          <Datagrid rowClick="edit" sx={{
            '& .RaDatagrid-headerCell': {
              textAlign: 'center',
            },
            '& .RaDatagrid-rowCell': {
              textAlign: 'center',
            },
          }}>
            <NumberField source="code" label="코드" sortable={false} />
            <CategoryNameField label="카테고리명" />
            <CategoryLevelField label="레벨" />
            <ReferenceField source="parentId" reference="categories" label="상위 카테고리" emptyText="-">
              <TextField source="name" />
            </ReferenceField>
            <ProductCountField label="상품 수" />
            <BooleanField source="isActive" label="활성화" />
            <EditButton />
            <DeleteButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// 카테고리 생성
export const CategoryCreate = () => (
  <Create>
    <SimpleForm>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextInput source="name" label="카테고리명" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <TextInput source="description" label="설명" multiline rows={3} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <BooleanInput source="isActive" label="활성화" defaultValue={true} />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            source="naverCategoryCode"
            label="네이버 쇼핑 카테고리 코드"
            helperText="네이버 쇼핑 파트너센터에서 확인한 8자리 카테고리 코드 (예: 50000167)"
            fullWidth
          />
        </Grid>
      </Grid>
    </SimpleForm>
  </Create>
);

// 카테고리 수정
export const CategoryEdit = () => {
  const record = useRecordContext();

  return (
    <Edit title="카테고리 수정">
      <SimpleForm>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput source="name" label="카테고리명" validate={required()} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextInput source="description" label="설명" multiline rows={3} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <BooleanInput source="isActive" label="활성화" defaultValue={true} />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source="naverCategoryCode"
              label="네이버 쇼핑 카테고리 코드"
              helperText="네이버 쇼핑 파트너센터에서 확인한 8자리 카테고리 코드 (예: 50000167)"
              fullWidth
            />
          </Grid>

          {/* 상품 수 표시 */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                상품 수
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {record?.productCount || 0}개
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};

// 카테고리 상세 보기
// CategoryShow 제거 - 편집 페이지에서 모든 정보를 확인할 수 있으므로 불필요